import React, { useState, useCallback, useRef, useEffect } from 'react';
import { CircleLoader } from '@hpstellar/core';
import {
    Accordion,
    AccordionHeader,
    AccordionBody,
    Input,
    Button,
    QuickFilterButton,
    Typography,
    Tooltip,
} from '@hpstellar/core/visId';
import { XCircle, ChevronUp, ChevronDown } from '@hpstellar/vis-icons';
import debounce from 'lodash/debounce';
import useCartState, { useCartActions } from '../../../hooks/useCartState';
import { dataLayerPush } from '../../../metrics/metrics-helpers';
import Recaptcha from '../../../shared/components/recaptcha';
import './promo-code.less';

const PromoCodeInput = ({ label = 'Enter promo code', couponLimit = 1, expanded }) => {
    const inputRef = useRef(null);
    const errorColor = '#cc2c00';
    const { cartInfo } = useCartState();
    const { cartDetails } = cartInfo || {};
    const { applyCouponMessages = [], captchaEnabledForCouponApply = [] } = cartDetails || {};
    const captchaConfig = captchaEnabledForCouponApply[0] || {};
    const { isCaptchaEnabled = false, recaptchaSiteKey } = captchaConfig;
    const shouldUseCaptcha = isCaptchaEnabled && recaptchaSiteKey;
    const { setPromoCode: setPromoCodeAction } = useCartActions();
    const [errorMessage, setErrorMessage] = useState();
    const [captchaToken, setCaptchaToken] = useState();

    const [pendingCoupon, setPendingCoupon] = useState();
    const [loading, setLoading] = useState(false);
    const [promoCode, setPromoCode] = useState('');

    useEffect(() => {
        try {
            if (!applyCouponMessages || applyCouponMessages.length === 0) {
                return;
            }
            //check if current promo code is in error state
            const errorCode = applyCouponMessages?.find(
                ({ type, promocode }) =>
                    type !== 'success' && promocode && promocode.toLowerCase() === inputRef.current.value.toLowerCase(),
            )?.message;
            if (errorCode) {
                setErrorMessage(errorCode);
            }
        } catch (e) {}
    }, [applyCouponMessages]);

    useEffect(() => {
        if (pendingCoupon) {
            //loop through applyCouponMessages to see if the pending coupon has been applied
            applyCouponMessages.forEach(({ promocode, type }) => {
                if (promocode && promocode.toLowerCase() === pendingCoupon.toLowerCase()) {
                    if (type !== 'success') {
                        dataLayerPush('e_couponError', { couponCode: pendingCoupon });
                    } else {
                        dataLayerPush('e_couponApplied', { couponCode: pendingCoupon });
                    }
                    setPendingCoupon(null);
                }
            });
        }
    }, [pendingCoupon, applyCouponMessages]);

    const onChange = useCallback(e => {
        setPromoCode(e.target.value);
    }, []);

    const submitPromoCode = useCallback(
        captchaToken => {
            setTimeout(() => {
                const code = promoCode.trim();
                if (!code) return;

                setLoading(true);
                setPendingCoupon(code);
                setPromoCodeAction(code, true, captchaToken)
                    .then(resp => {
                        setLoading(false);
                        if (resp?.errorMessage) {
                            setErrorMessage(resp.errorMessage);
                            setPendingCoupon(null);
                            if (shouldUseCaptcha && window.grecaptcha) window.grecaptcha.reset();
                            dataLayerPush('e_couponError', { couponCode: code });
                            setPromoCode('');
                        } else {
                            setErrorMessage(null);
                        }
                    })
                    .catch(err => {
                        setLoading(false);
                        if (shouldUseCaptcha && window.grecaptcha) window.grecaptcha.reset();
                        setPendingCoupon(null);
                        setPromoCode('');
                    });
            }, 0);
        },
        [promoCode, setPromoCodeAction, shouldUseCaptcha],
    );

    const onSubmit = useCallback(() => {
        const appliedCoupons = (applyCouponMessages || []).filter(({ type }) => type === 'success').length;
        if (appliedCoupons >= couponLimit) {
            dataLayerPush('e_couponError', { couponCode: promoCode });
            setErrorMessage('Only one coupon accepted');
            return;
        }
        setTimeout(() => {
            if (shouldUseCaptcha && window.grecaptcha && !captchaToken) {
                window.grecaptcha.execute();
            } else {
                // Submit without captcha token if captcha is disabled
                submitPromoCode(shouldUseCaptcha ? captchaToken : undefined);
            }
        }, 0);
    }, [promoCode, applyCouponMessages, captchaToken, shouldUseCaptcha]);

    const handleCaptcha = useCallback(
        captchaToken => {
            setCaptchaToken(captchaToken);
            submitPromoCode(captchaToken);
        },
        [submitPromoCode],
    );

    const onRemove = useCallback(
        code => {
            if (code === promoCode) {
                setTimeout(() => {
                    setPromoCode('');
                }, 100);
            }
        },
        [promoCode],
    );
    const onFocus = useCallback(() => {
        errorMessage && setErrorMessage(null);
    }, [errorMessage]);

    return (
        <>
            <div className="promo-code-container">
                <div className="promo-code-input">
                    <Input
                        slim={true}
                        onChange={onChange}
                        onFocus={onFocus}
                        label={label}
                        ref={inputRef}
                        value={promoCode}
                        error={errorMessage}
                        icon={
                            loading ? <CircleLoader /> : errorMessage ? <XCircle size="s" color={errorColor} /> : null
                        }
                    />
                    {errorMessage && (
                        <Typography className="error-msg" variant="footnote" style={{ color: errorColor }}>
                            {errorMessage}
                        </Typography>
                    )}
                </div>
                <Button disabled={!promoCode} size="small" onClick={onSubmit} success={loading}>
                    Apply
                </Button>
            </div>
            {shouldUseCaptcha && recaptchaSiteKey && (
                <Recaptcha
                    name="sendCPTcode"
                    callback={handleCaptcha}
                    siteKey={recaptchaSiteKey}
                    hide={!expanded}
                    position={'bottomLeft'}
                />
            )}
            <AppliedCoupons promoCode={promoCode} applyCouponMessages={applyCouponMessages} onRemove={onRemove} />
        </>
    );
};

const AppliedCouponButton = ({ code, onRemove = () => {} }) => {
    const [loading, setLoading] = useState(false);
    const { setPromoCode } = useCartActions();
    const resetLoading = useCallback(() => {
        setTimeout(() => {
            //delay so that cart state updates first with new list of coupons
            setLoading(false);
        }, 1000);
    }, []);
    const onClick = useCallback(() => {
        setTimeout(() => {
            setLoading(true);
            setPromoCode(code, false)
                .then(resp => {
                    resetLoading();
                    onRemove(code);
                })
                .catch(err => {
                    resetLoading();
                    onRemove(code);
                });
        }, 0);
    }, [code]);

    return loading ? (
        <CircleLoader />
    ) : (
        <QuickFilterButton isActive={!loading} onClick={onClick}>
            {code}
        </QuickFilterButton>
    );
};

const AppliedCoupons = ({ promoCode, applyCouponMessages, onRemove }) => {
    const numOfCoupons = applyCouponMessages?.length;
    const lastAddedCoupon =
        applyCouponMessages.find(({ code }) => code === promoCode) || applyCouponMessages[numOfCoupons - 1];
    const { message = 'Coupon successfully added.', toolTip, toolTipTitle, type } = lastAddedCoupon || {};
    const showMessage = type === 'success' && message;
    if (!applyCouponMessages || !applyCouponMessages.length) {
        return null;
    }

    return (
        <div className="applied-coupons">
            <div className="coupon-title">
                {toolTip && showMessage && <Tooltip content={toolTip} title={toolTipTitle} />}
                {showMessage && <Typography variant="footnote">{message}</Typography>}
            </div>
            <div className="applied-coupons-list">
                {applyCouponMessages.map(({ promocode: code, type }, i) => {
                    if (type !== 'success') {
                        return null;
                    }
                    return <AppliedCouponButton key={i} code={code} onRemove={onRemove} />;
                })}
            </div>
        </div>
    );
};

export default ({ title = 'Use a promo code', variation = 'accordion' }) => {
    const [expanded, setExpanded] = useState(false);
    const onChange = useCallback(v => {
        setExpanded(v);
    }, []);

    return (
        <div className={`promo-code ${variation}`}>
            {variation === 'accordion' ? (
                <Accordion borderPosition="top-bottom" onChange={onChange}>
                    <AccordionHeader aria-controls="accordionContent" id="accordionHeder" tag="span" title={title} />
                    <AccordionBody>
                        <PromoCodeInput expanded={expanded} />
                    </AccordionBody>
                </Accordion>
            ) : (
                <>
                    <Button
                        className="promo-toggle"
                        onClick={() => setExpanded?.(!expanded)}
                        aria-expanded={expanded}
                        variation="tertiary"
                        endIcon={expanded ? <ChevronUp /> : <ChevronDown />}
                        children={title}
                    />
                    {expanded && <PromoCodeInput expanded={expanded} />}
                </>
            )}
        </div>
    );
};
